import React, { useEffect, useRef, useState } from 'react';

const rateText = value => value == null ? '—' : `${(Number(value) * 100).toFixed(2)}%`;
const numberText = value => value == null ? '—' : Number(value).toLocaleString();
const rateDelta = (row, previous, metric) => row?.[metric] == null || previous?.[metric] == null ? null : (row[metric] - previous[metric]) * 100;
const deltaText = value => value == null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(2)} 个百分点`;

export default function KATrend({ rows = [], metric = 'completion_rate' }) {
  const launch = metric === 'launch_rate';
  const label = launch ? '上线交付率' : '完成率';
  const title = `每日${label}趋势`;
  const delta = (row, previous) => rateDelta(row, previous, metric);
  const [range, setRange] = useState(30);
  const [selectedDate, setSelectedDate] = useState('');
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(800);
  const visible = range ? rows.slice(-range) : rows;
  useEffect(() => {
    const node = chartRef.current;
    if (!node) return undefined;
    const measure = () => { if (node.clientWidth) setChartWidth(Math.max(280, Math.round(node.clientWidth))); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible.length]);
  const found = visible.findIndex(row => row.date === selectedDate);
  const index = found >= 0 ? found : visible.length - 1;
  const selected = visible[index];
  const originalIndex = selected ? rows.findIndex(row => row.date === selected.date) : -1;
  const change = delta(selected, rows[originalIndex - 1]);
  const rates = visible.filter(row => row[metric] != null).map(row => Number(row[metric]) * 100);
  const min = rates.length ? Math.min(...rates) : 0;
  const max = rates.length ? Math.max(...rates) : 100;
  const padding = Math.max(0.25, (max - min) * 0.18);
  const low = Math.max(0, Math.floor((min - padding) * 10) / 10);
  const high = Math.min(100, Math.ceil((max + padding) * 10) / 10);
  const span = high - low || 1;
  const plotWidth = chartWidth - 86;
  const x = i => 62 + i / Math.max(1, visible.length - 1) * plotWidth;
  const y = rate => 185 - (rate * 100 - low) / span * 160;
  const segments = [];
  let segment = [];
  visible.forEach((row, i) => {
    if (row[metric] == null) {
      if (segment.length) segments.push(segment);
      segment = [];
    } else segment.push({ x: x(i), y: y(row[metric]) });
  });
  if (segment.length) segments.push(segment);
  const dateTicks = [...new Set([0, Math.floor((visible.length - 1) / 2), visible.length - 1])].filter(i => i >= 0);
  return <section className="card ka-panel ka-trend-panel" aria-label={title}>
    <div className="ka-section-heading"><div><h2>{title}</h2><p className="ka-muted">按所选截至日期回看；悬停图表或使用日期滑块查看每日变化。</p></div>
      <div className="ka-segmented" aria-label="趋势范围">{[[30, '近 30 天'], [90, '近 90 天'], [0, '全部历史']].map(([value, label]) => <button type="button" key={value} aria-pressed={range === value} onClick={() => { setRange(value); setSelectedDate(''); }}>{label}</button>)}</div>
    </div>
    {visible.length ? <>
      <div className="ka-trend-readout" aria-label="所选日期指标">
        <div><small>所选日期</small><strong>{selected.date}</strong></div>
        <div><small>{label}</small><strong className="ka-rate-value">{rateText(selected[metric])}</strong></div>
        <div><small>较前一日</small><strong className={change > 0 ? 'ka-delta-positive' : change < 0 ? 'ka-delta-negative' : ''}>{deltaText(change)}</strong></div>
        <div><small>{launch ? '已上线 / 新增功能工单数' : '已完成 / 工单总数'}</small><strong>{numberText(selected[launch ? 'launched' : 'completed'])} / {numberText(selected[launch ? 'new_features' : 'total'])}</strong></div>
      </div>
      <svg ref={chartRef} role="img" aria-label={`${title}图，${visible[0].date} 至 ${visible[visible.length - 1].date}，纵轴 ${low.toFixed(1)}% 至 ${high.toFixed(1)}%，按数据范围缩放`} viewBox={`0 0 ${chartWidth} 222`} className="ka-trend">
        {[0, 1, 2, 3, 4].map(tick => {
          const value = low + span * tick / 4;
          const at = 185 - 160 * tick / 4;
          return <g key={tick}><line className="ka-chart-grid" x1="62" x2={chartWidth - 24} y1={at} y2={at} /><text x="52" y={at + 4} textAnchor="end">{value.toFixed(2)}%</text></g>;
        })}
        {dateTicks.map(i => <text key={i} x={x(i)} y="212" textAnchor={i === 0 ? 'start' : i === visible.length - 1 ? 'end' : 'middle'}>{visible[i].date.slice(5)}</text>)}
        {segments.map((points, i) => points.length > 1 ? <polyline key={i} points={points.map(point => `${point.x},${point.y}`).join(' ')} fill="none" stroke="currentColor" strokeWidth="2.5" vectorEffect="non-scaling-stroke" /> : <circle className="ka-isolated-point" key={i} cx={points[0].x} cy={points[0].y} r="3" fill="currentColor" />)}
        {selected[metric] != null && <g><line className="ka-chart-cursor" x1={x(index)} x2={x(index)} y1="25" y2="185" /><circle cx={x(index)} cy={y(selected[metric])} r="4.5" fill="currentColor" /></g>}
        {visible.map((row, i) => <rect key={row.date} x={Math.max(62, x(i) - plotWidth / 2 / Math.max(1, visible.length - 1))} y="15" width={visible.length === 1 ? plotWidth : plotWidth / (visible.length - 1) / (i === 0 || i === visible.length - 1 ? 2 : 1)} height="180" fill="transparent" onMouseEnter={() => setSelectedDate(row.date)}><title>{row.date}：{rateText(row[metric])}</title></rect>)}
      </svg>
      <div className="ka-trend-foot"><label>选择趋势日期<input aria-label="选择趋势日期" type="range" min="0" max={Math.max(0, visible.length - 1)} value={index} onChange={event => setSelectedDate(visible[Number(event.target.value)].date)} /></label><span>纵轴按数据范围缩放 · 区间最低 {rateText(rates.length ? min / 100 : null)} / 最高 {rateText(rates.length ? max / 100 : null)}</span></div>
      {!rates.length && <p className="ka-muted">此范围内没有可计算的{label}，{launch ? '零新增功能工单' : '零工单'}日期不连线。</p>}
      <details className="ka-trend-data"><summary>每日明细 · {visible.length} 天</summary><div className="ka-table-scroll ka-bounded-table"><table><thead><tr><th>日期</th><th>工单数</th><th>已完成</th><th>完成率</th><th>上线交付率</th><th>{label}日变化（百分点）</th></tr></thead><tbody>{visible.slice().reverse().map(row => {
        const at = rows.findIndex(item => item.date === row.date);
        return <tr key={row.date}><td>{row.date}</td><td>{numberText(row.total)}</td><td>{numberText(row.completed)}</td><td>{rateText(row.completion_rate)}</td><td>{rateText(row.launch_rate)}</td><td>{deltaText(delta(row, rows[at - 1]))}</td></tr>;
      })}</tbody></table></div></details>
    </> : <p className="ka-muted">当前筛选条件没有趋势数据。</p>}
  </section>;
}
